import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  IconButton,
  Alert,
  LinearProgress,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Chip
} from '@mui/material';
import {
  Close as CloseIcon,
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  Check as CheckIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Dataset as DatasetIcon,
  ExpandMore as ExpandMoreIcon,
  InsertDriveFileOutlined as FileIcon,
  WarningAmber as WarningIcon
} from '@mui/icons-material';
import { useLead } from '../../contexts/LeadContext';

const T = {
  green: '#0a6b3e',
  greenLt: '#e5f5ec',
  greenMd: '#0d8a50',
  greenSf: 'rgba(10,107,62,0.06)',
  ink: '#0f1a13',
  ink2: '#253b2c',
  subtle: '#526b5c',
  muted: '#8eaa98',
  bg: '#f5f8f6',
  border: '#dce8e1',
  borderL: '#eaf1ec',
  white: '#ffffff',
  red: '#dc2626',
  redLt: '#fef2f2',
  redBorder: '#fecaca',
  amber: '#d97706',
  amberLt: '#fffbeb',
  amberBorder: '#fde68a',
  blue: '#2563eb',
  blueLt: '#eff6ff',
  blueBorder: '#bfdbfe',
  font: "'Inter','DM Sans','Plus Jakarta Sans',sans-serif",
};

const normalizeIndianMobile = (value) => {
  if (!value) return { value: '', isValid: false, error: null };
  const digits = value.toString().replace(/\D/g, '');
  const last10 = digits.length > 10 ? digits.slice(-10) : digits;
  if (last10.length !== 10) {
    return { value: last10, isValid: false, error: `Invalid mobile number length. Must be 10 digits. Got: ${last10.length}` };
  }
  if (!/^[6-9]\d{9}$/.test(last10)) {
    return { value: last10, isValid: false, error: `Invalid Indian mobile number. Must start with 6, 7, 8, or 9. Got: ${last10}` };
  }
  return { value: last10, isValid: true, error: null };
};

const CSVImport = ({
  open,
  onClose,
  onImportComplete,
  title = 'Import Leads from CSV',
  endpoint = '/lms/leads/import/',
  defaultFilters = {}
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [fileReadError, setFileReadError] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [importResults, setImportResults] = useState(null);
  const [validationResults, setValidationResults] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const [isValidated, setIsValidated] = useState(false);
  const [csvData, setCsvData] = useState({ headers: [], rows: [] });
  const [originalCsvText, setOriginalCsvText] = useState(null);
  const [showAllRows, setShowAllRows] = useState(false);
  const [invalidMobileRows, setInvalidMobileRows] = useState(new Set());
  const [mobileValidationWarnings, setMobileValidationWarnings] = useState([]);
  const [invalidBudgetRows, setInvalidBudgetRows] = useState(new Set());
  const [budgetValidationWarnings, setBudgetValidationWarnings] = useState([]);
  const [importProgress, setImportProgress] = useState(null);
  const [isPolling, setIsPolling] = useState(false);
  const [existingQueuedJob, setExistingQueuedJob] = useState(null);
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);
  const pollingIntervalRef = useRef(null);
  const fileInputRef = useRef(null);
  const { validateLeadImport, commitLeadImport, getLeadImportStatus, discardLeadImport } = useLead();
  const MAX_ROWS = 5000;
  const VALID_STAGES = ['new', 'qualified', 'negotiation', 'closure', 'contacted', 'junk', 'archived'];

  const buildExtraPayload = () =>
    defaultFilters && typeof defaultFilters === 'object' ? defaultFilters : {};

  const LEAD_STATUS_FLOW = {
    new: ['unassigned', 'assigned'],
    contacted: ['contacted', 'no_response', 'unreachable', 'not_interested'],
    qualified: ['follow_up_scheduled', 'property_details_shared', 'site_visit_scheduled', 'site_visit_completed'],
    negotiation: ['site_visit_scheduled', 'site_visit_completed', 'shortlisted', 'offer_made'],
    closure: ['closed_won', 'lost', 'flaged'],
    junk: ['junk', 'not_interested', 'not_looking_currently', 'cold_lead'],
    archived: ['archived']
  };

  const steps = ['Upload File', 'Validate & Preview', 'Import'];

  // ── CSV Helpers ──────────────────────────────────────────────────────────

  const sanitizeCellValue = (value = '') =>
    String(value).replace(/^\ufeff/, '').replace(/^"(.*)"$/, '$1').replace(/^'(.*)'$/, '$1').trim();

  const detectDelimiter = (text) => {
    const delimiterCandidates = [',', ';', '\t', '|'];
    let headerLine = '';
    let inQuotes = false;
    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      if (char === '"') {
        const nextChar = text[i + 1];
        if (inQuotes && nextChar === '"') { i += 1; } else { inQuotes = !inQuotes; }
        continue;
      }
      if ((char === '\n' || char === '\r') && !inQuotes) break;
      headerLine += char;
    }
    const sanitizedHeader = headerLine.replace(/^\ufeff/, '');
    let selectedDelimiter = ',';
    let bestScore = -1;
    delimiterCandidates.forEach((candidate) => {
      const score = sanitizedHeader.split(candidate).length - 1;
      if (score > bestScore) { bestScore = score; selectedDelimiter = candidate; }
    });
    return bestScore > 0 ? selectedDelimiter : ',';
  };

  const tokenizeCSV = (text, delimiter) => {
    const rows = [];
    let currentField = '';
    let currentRow = [];
    let inQuotes = false;
    let rowNumber = 1;
    let lastCharWasDelimiter = false;
    const pushField = () => { currentRow.push(currentField); currentField = ''; };
    const pushRow = () => { rows.push({ values: currentRow, rowNumber }); currentRow = []; rowNumber += 1; };
    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      if (char === '"') {
        const nextChar = text[i + 1];
        if (inQuotes && nextChar === '"') { currentField += '"'; i += 1; } else { inQuotes = !inQuotes; }
        lastCharWasDelimiter = false;
        continue;
      }
      if (char === delimiter && !inQuotes) { pushField(); lastCharWasDelimiter = true; continue; }
      if (char === '\n' && !inQuotes) { pushField(); pushRow(); lastCharWasDelimiter = false; continue; }
      currentField += char;
      lastCharWasDelimiter = false;
    }
    if (currentField.length > 0 || currentRow.length > 0 || lastCharWasDelimiter) { pushField(); pushRow(); }
    return rows;
  };

  const parseCSV = (csvText) => {
    try {
      const normalizedText = csvText.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
      if (!normalizedText.trim()) throw new Error('CSV file is empty');
      const delimiter = detectDelimiter(normalizedText);
      const parsedRows = tokenizeCSV(normalizedText, delimiter);
      if (!parsedRows.length) throw new Error('CSV file is empty');

      const rawHeaders = parsedRows[0].values.map((header, index) => {
        let cleanHeader = sanitizeCellValue(header);
        if (index === 0 && cleanHeader && cleanHeader.charCodeAt(0) === 0xFEFF) cleanHeader = cleanHeader.substring(1);
        return cleanHeader;
      });
      const errorColumnIndex = rawHeaders.findIndex((header) => header.toLowerCase() === 'error');
      const headers = rawHeaders
        .filter((header) => header.toLowerCase() !== 'error')
        .map((h) => h.trim().toLowerCase());

      const dataRows = parsedRows.slice(1);
      const dataRowCount = dataRows.reduce((count, rowEntry) => {
        const hasContent = rowEntry.values.some((value) => { const sanitized = sanitizeCellValue(value); return sanitized && sanitized.trim() !== ''; });
        return hasContent ? count + 1 : count;
      }, 0);

      if (dataRowCount > MAX_ROWS) {
        throw new Error(`CSV file contains ${dataRowCount} rows. Maximum allowed is ${MAX_ROWS} rows. Please split your file into smaller files.`);
      }

      const processedRows = [];
      let rowValidationErrors = [];
      const invalidMobileRowIndices = new Set();
      const mobileWarnings = [];
      const invalidBudgetRowIndices = new Set();
      const budgetWarnings = [];

      for (const rowEntry of dataRows) {
        const { values: rawValues, rowNumber } = rowEntry;
        const sanitizedRow = rawValues.map((value) => sanitizeCellValue(value));
        if (errorColumnIndex !== -1 && errorColumnIndex < sanitizedRow.length) sanitizedRow.splice(errorColumnIndex, 1);
        const allFieldsEmpty = sanitizedRow.every((field) => !field || field.trim() === '');
        if (allFieldsEmpty) continue;

        const rowObj = {};
        const currentRowErrors = [];
        let hasEmail = false;
        let hasMobile = false;

        headers.forEach((header, index) => {
          if (!header) return;
          let value = index < sanitizedRow.length ? sanitizedRow[index] : '';
          if (header.toLowerCase() === 'tags') {
            if (value) {
              try {
                const cleanValue = value.replace(/^"(.*)"$/, '$1');
                rowObj[header] = cleanValue.split(',').map((tag) => tag.trim()).filter((tag) => tag.length > 0);
              } catch (e) { rowObj[header] = []; }
            } else { rowObj[header] = []; }
          } else if (['mobile', 'channel_partner_phone', 'agent_mobile'].includes(header.toLowerCase())) {
            if (value) {
              const { value: normalized, isValid, error } = normalizeIndianMobile(value);
              rowObj[header] = normalized;
              if (header.toLowerCase() === 'mobile' && isValid) hasMobile = true;
              if (!isValid && normalized) currentRowErrors.push({ field: header.toLowerCase(), message: error });
            } else { rowObj[header] = ''; }
          } else if (header.toLowerCase() === 'email') {
            rowObj[header] = value;
            if (value && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) hasEmail = true;
          } else if (header.toLowerCase() === 'stage') {
            const stageValue = value.toLowerCase();
            if (stageValue && !VALID_STAGES.includes(stageValue)) {
              currentRowErrors.push({ field: 'stage', value, message: `Invalid stage "${value}". Valid stages are: ${VALID_STAGES.join(', ')}` });
            }
            rowObj[header] = stageValue || 'new';
          } else if (header.toLowerCase() === 'name') {
            if (!value) currentRowErrors.push({ field: 'name', message: 'Name is required' });
            rowObj[header] = value;
          } else { rowObj[header] = value; }
        });

        if (!hasEmail && !hasMobile) {
          currentRowErrors.push({ field: 'contact', message: `Either email or mobile is required. Email: "${rowObj['email'] || ''}", Mobile: "${rowObj['mobile'] || ''}"` });
        }

        const budgetMin = rowObj['budget_min_in_lacs'];
        const budgetMax = rowObj['budget_max_in_lacs'];
        if (budgetMin !== undefined && budgetMin !== '' && budgetMax !== undefined && budgetMax !== '') {
          const minVal = typeof budgetMin === 'number' ? budgetMin : parseFloat(budgetMin);
          const maxVal = typeof budgetMax === 'number' ? budgetMax : parseFloat(budgetMax);
          if (!isNaN(minVal) && !isNaN(maxVal) && maxVal <= minVal) {
            currentRowErrors.push({ field: 'budget_max_in_lacs', message: `budget_max_in_lacs (${maxVal}) must be greater than budget_min_in_lacs (${minVal})` });
          }
        }

        const mobileErrors = currentRowErrors.filter((err) => ['mobile', 'channel_partner_phone', 'agent_mobile'].includes(err.field));
        if (mobileErrors.length > 0) {
          invalidMobileRowIndices.add(processedRows.length);
          mobileErrors.forEach((mobileError) => { mobileWarnings.push({ row: rowNumber, field: mobileError.field, mobile: rowObj[mobileError.field] || '', message: mobileError.message }); });
        }

        const budgetErrors = currentRowErrors.filter((err) => ['budget_min_in_lacs', 'budget_max_in_lacs'].includes(err.field));
        if (budgetErrors.length > 0) {
          invalidBudgetRowIndices.add(processedRows.length);
          budgetErrors.forEach((budgetError) => { budgetWarnings.push({ row: rowNumber, field: budgetError.field, message: budgetError.message }); });
        }

        const nonMobileBudgetErrors = currentRowErrors.filter(
          (err) => !['mobile', 'channel_partner_phone', 'agent_mobile', 'budget_min_in_lacs', 'budget_max_in_lacs'].includes(err.field)
        );
        if (nonMobileBudgetErrors.length > 0) {
          rowValidationErrors.push({ row: rowNumber, data: rowObj, errors: nonMobileBudgetErrors });
        }
        processedRows.push(rowObj);
      }

      if (rowValidationErrors.length > 0) {
        const errorMessage = rowValidationErrors
          .map((rowError) => `Row ${rowError.row}:\n${rowError.errors.map((error) => `${error.field}: ${error.message}`).join('\n')}`)
          .join('\n\n');
        throw new Error(errorMessage);
      }

      return { headers, rows: processedRows, invalidMobileRowIndices, mobileWarnings, invalidBudgetRowIndices, budgetWarnings };
    } catch (error) {
      console.error('Error parsing CSV:', error);
      throw error;
    }
  };

  // ── Downloads ────────────────────────────────────────────────────────────

  const downloadFailedImportsCSV = () => {
    if (!importResults || !importResults.failed_imports || importResults.failed_imports.length === 0) return;
    const headerSet = new Set();
    importResults.failed_imports.forEach(({ row }) => { if (row && typeof row === 'object') Object.keys(row).forEach((key) => headerSet.add(key)); });
    const headers = Array.from(headerSet);
    const escapeValue = (value) => {
      if (value === null || value === undefined) return '';
      let stringValue = value;
      if (Array.isArray(value)) stringValue = value.join('; ');
      else if (typeof value === 'object') stringValue = JSON.stringify(value);
      else stringValue = String(value);
      return `"${stringValue.replace(/"/g, '""')}"`;
    };
    const csvRows = [[...headers, 'error'].map(escapeValue).join(',')];
    importResults.failed_imports.forEach(({ row = {}, error }) => {
      csvRows.push([...headers.map((header) => escapeValue(row[header])), escapeValue(error || '')].join(','));
    });
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `failed_imports_${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const downloadCSVWithErrors = () => {
    if (!originalCsvText || !csvData.headers || csvData.headers.length === 0) return;
    const normalizeMobile = (value) => { const { value: normalized } = normalizeIndianMobile(value); return normalized || ''; };
    const getRowKey = (row) => { const email = (row.email || '').toString().trim().toLowerCase(); const mobile = normalizeMobile(row.mobile || ''); return email || mobile || null; };
    const errorMap = new Map();
    const failedList = Array.isArray(validationResults?.failed_imports) ? validationResults.failed_imports : [];
    if (validationResults && failedList.length > 0) {
      failedList.forEach(({ row, error }) => { if (row) { const key = getRowKey(row); if (key) { const existing = errorMap.get(key) || ''; errorMap.set(key, existing ? `${existing}; ${error || ''}` : error || ''); } } });
    }
    csvData.rows.forEach((row, index) => {
      const errors = [];
      const rowKey = getRowKey(row);
      if (invalidMobileRows.has(index)) {
        const mobileErrors = mobileValidationWarnings.filter((w) => { if (w.row === index + 1) return true; if (rowKey && w.mobile) { const warningMobile = normalizeMobile(w.mobile); return rowKey === warningMobile || (row.email && row.email.toLowerCase() === rowKey); } return false; });
        if (mobileErrors.length > 0) errors.push(...mobileErrors.map((e) => `${e.field}: ${e.message}`));
      }
      if (invalidBudgetRows.has(index)) {
        const budgetErrors = budgetValidationWarnings.filter((w) => w.row === index + 1);
        if (budgetErrors.length > 0) errors.push(...budgetErrors.map((e) => `${e.field}: ${e.message}`));
      }
      if (errors.length > 0 && rowKey) { const existing = errorMap.get(rowKey) || ''; errorMap.set(rowKey, existing ? `${existing}; ${errors.join('; ')}` : errors.join('; ')); }
    });

    const normalizedText = originalCsvText.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    const delimiter = detectDelimiter(normalizedText);
    const parsedRows = tokenizeCSV(normalizedText, delimiter);
    if (parsedRows.length === 0) return;
    const escapeValue = (value) => { if (value === null || value === undefined) return ''; return `"${String(value).replace(/"/g, '""')}"`; };
    const csvRows = [];
    const originalHeaders = parsedRows[0].values.map((header, index) => { let cleanHeader = sanitizeCellValue(header); if (index === 0 && cleanHeader && cleanHeader.charCodeAt(0) === 0xFEFF) cleanHeader = cleanHeader.substring(1); return cleanHeader; });
    const emailColumnIndex = originalHeaders.findIndex((h) => h.toLowerCase() === 'email');
    const mobileColumnIndex = originalHeaders.findIndex((h) => h.toLowerCase() === 'mobile');
    const hasErrorColumn = originalHeaders.some((h) => h.toLowerCase() === 'error');
    const headersToUse = hasErrorColumn ? originalHeaders : [...originalHeaders, 'error'];
    csvRows.push(headersToUse.map(escapeValue).join(','));
    parsedRows.slice(1).forEach((rowEntry) => {
      const sanitizedRow = rowEntry.values.map((value) => sanitizeCellValue(value));
      const allFieldsEmpty = sanitizedRow.every((field) => !field || field.trim() === '');
      if (allFieldsEmpty) return;
      const email = emailColumnIndex !== -1 && emailColumnIndex < sanitizedRow.length ? sanitizedRow[emailColumnIndex].trim().toLowerCase() : '';
      const mobileRaw = mobileColumnIndex !== -1 && mobileColumnIndex < sanitizedRow.length ? sanitizedRow[mobileColumnIndex] : '';
      const mobile = normalizeMobile(mobileRaw);
      const matchKey = email || mobile || null;
      const error = matchKey ? errorMap.get(matchKey) || '' : '';
      const rowValues = [...sanitizedRow];
      if (hasErrorColumn) { const errorIndex = originalHeaders.findIndex((h) => h.toLowerCase() === 'error'); if (errorIndex !== -1) { while (rowValues.length <= errorIndex) rowValues.push(''); rowValues[errorIndex] = error; } }
      else rowValues.push(error);
      csvRows.push(rowValues.map(escapeValue).join(','));
    });
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `leads_with_errors_${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const downloadTemplateCSV = () => {
    const csvContent = [
      'name,email,mobile,source,stage,status,budget_min_in_lacs,budget_max_in_lacs,tags,notes,channel_partner_name,channel_partner_phone,agent_name,agent_mobile',
      `Ananya Iyer,ananya.iyer@example.com,9765123409,Google Display Ads,new,unassigned,8,16,"priority","Needs call today",Ramji Properties,9876543210,John Doe,9876543210`,
      `Tanya Malhotra,tanya.m@example.com,9986034123,Facebook,qualified,assigned,6,14,"warm","Wants floor plan",,,Jane Smith,9876543211`,
      `Dev Sharma,dev.sharma@example.com,9897213456,referral,negotiation,assigned,5,12,"interested","Requested site pics",ABC Properties,9876543212,Bob Johnson,9876543212`
    ].join('\n').trim();
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.setAttribute('hidden', '');
    a.setAttribute('href', url);
    a.setAttribute('download', 'leads_import_template.csv');
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleFileChange = (event) => {
    setUploadError(null);
    setFileReadError(false);
    const file = event.target.files[0];
    if (file) {
      if (!file.name.endsWith('.csv')) { setUploadError('Only CSV files are allowed'); setFileReadError(true); if (fileInputRef.current) fileInputRef.current.value = ''; return; }
      if (file.size > 5 * 1024 * 1024) { setUploadError('File size exceeds the maximum of 5 MB'); setFileReadError(true); if (fileInputRef.current) fileInputRef.current.value = ''; return; }
      setSelectedFile(file);
      setValidationResults(null);
      setIsValidated(false);
      setImportResults(null);
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const csvText = e.target.result;
          setOriginalCsvText(csvText);
          const parsedData = parseCSV(csvText);
          setInvalidMobileRows(parsedData.invalidMobileRowIndices || new Set());
          setMobileValidationWarnings(parsedData.mobileWarnings || []);
          setInvalidBudgetRows(parsedData.invalidBudgetRowIndices || new Set());
          setBudgetValidationWarnings(parsedData.budgetWarnings || []);
          setCsvData({ headers: parsedData.headers, rows: parsedData.rows });
          setFileReadError(false);
          setActiveStep(1);
        } catch (error) {
          console.error('Error processing CSV:', error);
          setUploadError(error.message || 'Error processing CSV file');
          setFileReadError(true);
          if (fileInputRef.current) fileInputRef.current.value = '';
          setSelectedFile(null);
          setCsvData({ headers: [], rows: [] });
          setOriginalCsvText(null);
        }
      };
      reader.onerror = () => {
        setUploadError('Error reading file');
        setFileReadError(true);
        if (fileInputRef.current) fileInputRef.current.value = '';
        setSelectedFile(null);
        setCsvData({ headers: [], rows: [] });
      };
      reader.readAsText(file);
    }
  };

  const resetForm = () => {
    if (pollingIntervalRef.current) { clearInterval(pollingIntervalRef.current); pollingIntervalRef.current = null; }
    setSelectedFile(null);
    setUploadProgress(0);
    setIsUploading(false);
    setIsValidating(false);
    setIsPolling(false);
    setUploadError(null);
    setFileReadError(false);
    setActiveStep(0);
    setImportResults(null);
    setImportProgress(null);
    setValidationResults(null);
    setIsValidated(false);
    setCsvData({ headers: [], rows: [] });
    setOriginalCsvText(null);
    setShowAllRows(false);
    setInvalidMobileRows(new Set());
    setMobileValidationWarnings([]);
    setInvalidBudgetRows(new Set());
    setBudgetValidationWarnings([]);
    setExistingQueuedJob(null);
    setIsCheckingStatus(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDialogClose = () => {
    if (pollingIntervalRef.current) { clearInterval(pollingIntervalRef.current); pollingIntervalRef.current = null; }
    setIsPolling(false);
    if (localStorage.getItem('activeLeadImportJob')) window.dispatchEvent(new CustomEvent('leadImportStarted'));
    resetForm();
    onClose();
  };

  const getValidRows = () => {
    if (!csvData.rows || csvData.rows.length === 0) return [];
    const failedImportsList = Array.isArray(validationResults?.failed_imports) ? validationResults.failed_imports : [];
    if (!validationResults || failedImportsList.length === 0) {
      return csvData.rows.filter((row, index) => !invalidMobileRows.has(index) && !invalidBudgetRows.has(index));
    }
    const failedRowNumbers = new Set(failedImportsList.map((item) => item.row_number || item.rowNumber));
    return csvData.rows.filter((row, index) => {
      const rowNumber = index + 1;
      return !failedRowNumbers.has(rowNumber) && !invalidMobileRows.has(index) && !invalidBudgetRows.has(index);
    });
  };

  const handleValidate = async () => {
    if (!selectedFile) { setUploadError('Please select a CSV file to validate'); return; }
    if (!csvData.rows.length) { setUploadError('No rows detected in the selected CSV file'); return; }
    try {
      setIsValidating(true);
      setUploadError(null);
      const result = await validateLeadImport(csvData.rows, buildExtraPayload());
      const validationData = {
        total_successful: result.total_successful || 0,
        total_failed: result.total_failed || 0,
        failed_imports: result.failed_imports || [],
        failed_imports_truncated: result.failed_imports_truncated || false
      };
      setValidationResults(validationData);
      setIsValidated(true);
      if (validationData.total_successful === 0) {
        setUploadError('No valid entries found. Please fix all errors and re-validate.');
      } else if (validationData.total_failed > 0) {
        setUploadError(`${validationData.total_successful} row(s) valid, ${validationData.total_failed} row(s) have errors.`);
      } else {
        setUploadError(null);
      }
    } catch (error) {
      console.error('Error validating CSV:', error);
      let errorMessage = 'Failed to validate CSV.';
      if (error.response) {
        const data = error.response.data;
        if (data) {
          if (data.error_detail) errorMessage = data.error_detail;
          else if (data.error) errorMessage = data.error;
          else if (typeof data === 'string') errorMessage = data;
        }
      } else if (error.request) {
        if (error.code === 'ECONNABORTED' || error.message.includes('timeout'))
          errorMessage = 'Request timed out. The CSV file might be too large. Please try with a smaller file.';
        else errorMessage = 'No response received from server. Please check your network connection.';
      } else { errorMessage = `Error: ${error.message}`; }
      setUploadError(errorMessage);
      if (error.response?.status === 400 && error.response?.data) {
        const data = error.response.data;
        const failedList = Array.isArray(data.failed_imports) ? data.failed_imports : [];
        setValidationResults({ total_successful: data.total_successful ?? 0, total_failed: data.total_failed ?? failedList.length, failed_imports: failedList, failed_imports_truncated: data.failed_imports_truncated || false });
      } else { setValidationResults(null); }
      setIsValidated(false);
    } finally { setIsValidating(false); }
  };

  const startPolling = () => {
    if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
    setIsPolling(true);
    const poll = async () => {
      try {
        const status = await getLeadImportStatus();
        setImportProgress(status);
        if (status.status === 'completed' || status.status === 'failed') {
          if (pollingIntervalRef.current) { clearInterval(pollingIntervalRef.current); pollingIntervalRef.current = null; }
          setIsPolling(false);
          const finalResults = { status: status.status, total_successful: status.success_count, total_failed: status.error_count, successful_imports: [], failed_imports: status.error_rows || status.errors || [] };
          setImportResults(finalResults);
          setActiveStep(2);
          if (onImportComplete) onImportComplete(finalResults);
        }
      } catch (error) { console.error('Error polling import status:', error); }
    };
    poll();
    pollingIntervalRef.current = setInterval(poll, 3000);
  };

  React.useEffect(() => { return () => { if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current); }; }, []);

  React.useEffect(() => {
    if (open) {
      checkExistingJob();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const checkExistingJob = async () => {
    try {
      setIsCheckingStatus(true);
      const jobStatus = await getLeadImportStatus();
      if (jobStatus.status === 'running') {
        setImportProgress(jobStatus);
        setActiveStep(2);
        startPolling();
      } else if (jobStatus.status === 'queued') {
        setExistingQueuedJob(jobStatus);
      }
    } catch (error) {
      console.error('Error checking existing import job:', error);
    } finally {
      setIsCheckingStatus(false);
    }
  };

  const handleCommitExisting = async () => {
    try {
      setIsUploading(true);
      setUploadError(null);
      await commitLeadImport();
      setExistingQueuedJob(null);
      localStorage.setItem('activeLeadImportJob', 'active');
      window.dispatchEvent(new CustomEvent('leadImportStarted'));
      setActiveStep(2);
      setIsUploading(false);
      startPolling();
    } catch (error) {
      let errorMessage = 'Failed to start import. Please try again.';
      if (error.response?.data?.error) errorMessage = error.response.data.error;
      setUploadError(errorMessage);
      setIsUploading(false);
    }
  };

  const handleImport = async () => {
    if (!selectedFile) { setUploadError('Please select a CSV file'); return; }
    if (!isValidated) { setUploadError('Please validate your CSV first before importing.'); return; }
    try {
      setIsUploading(true);
      setUploadError(null);
      await commitLeadImport();
      localStorage.setItem('activeLeadImportJob', 'active');
      window.dispatchEvent(new CustomEvent('leadImportStarted'));
      setUploadError(null);
      setActiveStep(2);
      setIsUploading(false);
      startPolling();
    } catch (error) {
      console.error('Error committing import:', error);
      let errorMessage = 'Failed to start import. Please try again.';
      if (error.response) { if (error.response.data?.error) errorMessage = error.response.data.error; else if (typeof error.response.data === 'string') errorMessage = error.response.data; }
      else if (error.request) errorMessage = 'No response received from server. Please check your network connection.';
      else errorMessage = `Error: ${error.message}`;
      setUploadError(errorMessage);
      setIsUploading(false);
    }
  };

  // ── Shared UI components ─────────────────────────────────────────────────

  const InfoChip = ({ icon, label, value, color = T.green, bg = T.greenLt }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.75, borderRadius: '8px', bgcolor: bg }}>
      {icon}
      <Typography sx={{ fontFamily: T.font, fontSize: 12, fontWeight: 600, color }}>{value}</Typography>
      <Typography sx={{ fontFamily: T.font, fontSize: 11, color: T.subtle }}>{label}</Typography>
    </Box>
  );

  // ── Step 0: Upload ──────────────────────────────────────────────────────

  const renderStep0 = () => (
    <Box sx={{ py: 2 }}>
      <input ref={fileInputRef} type="file" accept=".csv" onChange={handleFileChange} style={{ display: 'none' }} />

      {/* Drop zone — compact */}
      <Box
        onClick={() => fileInputRef.current?.click()}
        sx={{
          border: `2px dashed ${T.border}`,
          borderRadius: 2.5,
          px: 3, py: 2.5,
          mb: 2,
          textAlign: 'center',
          cursor: 'pointer',
          transition: 'all 0.2s',
          bgcolor: T.bg,
          '&:hover': { borderColor: T.green, bgcolor: T.greenSf }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
          <Box sx={{ width: 40, height: 40, borderRadius: '10px', bgcolor: T.greenLt, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <UploadIcon sx={{ fontSize: 20, color: T.green }} />
          </Box>
          <Box sx={{ textAlign: 'left' }}>
            <Typography sx={{ fontFamily: T.font, fontSize: 13.5, fontWeight: 600, color: T.ink }}>
              Click to select a CSV file
            </Typography>
            <Typography sx={{ fontFamily: T.font, fontSize: 11.5, color: T.muted }}>
              Max {MAX_ROWS.toLocaleString()} rows &middot; 5 MB &middot; .csv
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Format guide — inline row */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
        <Box sx={{ flex: 1, px: 1.5, py: 1, borderRadius: 1.5, border: `1px solid ${T.redBorder}`, bgcolor: T.redLt }}>
          <Typography sx={{ fontFamily: T.font, fontSize: 10.5, fontWeight: 700, color: T.red, textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.25 }}>Required</Typography>
          <Typography sx={{ fontFamily: T.font, fontSize: 12, color: T.ink2, lineHeight: 1.5 }}><strong>name</strong>, <strong>email</strong> or <strong>mobile</strong></Typography>
        </Box>
        <Box sx={{ flex: 1, px: 1.5, py: 1, borderRadius: 1.5, border: `1px solid ${T.border}`, bgcolor: T.greenLt }}>
          <Typography sx={{ fontFamily: T.font, fontSize: 10.5, fontWeight: 700, color: T.green, textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.25 }}>Optional</Typography>
          <Typography sx={{ fontFamily: T.font, fontSize: 12, color: T.ink2, lineHeight: 1.5 }}>source, stage, budget, tags, notes, agent</Typography>
        </Box>
      </Box>

      {/* Stage/status reference — compact two-column table */}
      <Box sx={{ borderRadius: 2, border: `1px solid ${T.borderL}`, overflow: 'hidden', mb: 1.5 }}>
        <Box sx={{ px: 1.5, py: 0.75, bgcolor: T.bg, borderBottom: `1px solid ${T.borderL}` }}>
          <Typography sx={{ fontFamily: T.font, fontSize: 10.5, fontWeight: 700, color: T.subtle, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Valid Status per Stage
          </Typography>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', '& > *:nth-of-type(odd)': { borderRight: `1px solid ${T.borderL}` } }}>
          {Object.entries(LEAD_STATUS_FLOW).map(([stage, statuses], idx, arr) => (
            <Box
              key={stage}
              sx={{
                display: 'flex', alignItems: 'baseline', gap: 0.75, px: 1.5, py: 0.6,
                borderBottom: idx < arr.length - 2 ? `1px solid ${T.borderL}` : 'none',
              }}
            >
              <Typography sx={{ fontFamily: T.font, fontSize: 11, fontWeight: 700, color: T.green, whiteSpace: 'nowrap', minWidth: 68 }}>
                {stage}
              </Typography>
              <Typography sx={{ fontFamily: T.font, fontSize: 10.5, color: T.subtle, lineHeight: 1.4 }}>
                {statuses.join(', ')}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {uploadError && (
        <Alert severity="error" sx={{ mb: 1.5, fontFamily: T.font, fontSize: 12.5, '& .MuiAlert-message': { whiteSpace: 'pre-wrap' } }}>
          {uploadError}
        </Alert>
      )}
    </Box>
  );

  // ── Step 1: Validate & Preview ──────────────────────────────────────────

  const renderStep1 = () => {
    const failedImports = Array.isArray(validationResults?.failed_imports) ? validationResults.failed_imports : [];

    return (
      <Box sx={{ py: 2 }}>
        {/* File summary bar */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.5, borderRadius: 2, bgcolor: T.bg, border: `1px solid ${T.borderL}`, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ width: 36, height: 36, borderRadius: '10px', bgcolor: T.greenLt, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FileIcon sx={{ fontSize: 18, color: T.green }} />
            </Box>
            <Box>
              <Typography sx={{ fontFamily: T.font, fontSize: 13, fontWeight: 600, color: T.ink }}>{selectedFile?.name}</Typography>
              <Typography sx={{ fontFamily: T.font, fontSize: 11, color: T.muted }}>
                {(selectedFile?.size / 1024).toFixed(1)} KB &middot; {csvData.rows.length} rows
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {validationResults && (
              <>
                <InfoChip label="valid" value={validationResults.total_successful} />
                {validationResults.total_failed > 0 && (
                  <InfoChip label="errors" value={validationResults.total_failed} color={T.red} bg={T.redLt} icon={<ErrorIcon sx={{ fontSize: 14, color: T.red }} />} />
                )}
              </>
            )}
            {!validationResults && (
              <InfoChip label="rows" value={csvData.rows.length} />
            )}
          </Box>
        </Box>

        {uploadError && (
          <Alert
            severity={isValidated && validationResults?.total_successful > 0 ? 'warning' : 'error'}
            sx={{ mb: 2, fontFamily: T.font, fontSize: 12.5, borderRadius: 2 }}
          >
            {uploadError}
          </Alert>
        )}

        {/* Preview table */}
        {csvData.headers.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontFamily: T.font, fontSize: 12, fontWeight: 700, color: T.subtle, mb: 1, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Preview
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 280, borderRadius: 2, border: `1px solid ${T.borderL}`, boxShadow: 'none' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontFamily: T.font, fontWeight: 700, fontSize: 11, color: T.subtle, bgcolor: T.bg, position: 'sticky', left: 0, zIndex: 3, minWidth: 44, maxWidth: 44 }}>#</TableCell>
                    {csvData.headers.map((header, index) => (
                      <TableCell key={index} sx={{ fontFamily: T.font, fontWeight: 700, fontSize: 11, color: T.subtle, bgcolor: T.bg, whiteSpace: 'nowrap' }}>
                        {header}
                        {['name', 'email', 'mobile'].includes(header.toLowerCase()) && (
                          <Typography component="span" sx={{ color: T.red, fontSize: 9, ml: 0.3, verticalAlign: 'super' }}>*</Typography>
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(showAllRows ? csvData.rows : csvData.rows.slice(0, 100)).map((row, rowIndex) => {
                    const isInvalid = invalidMobileRows.has(rowIndex) || invalidBudgetRows.has(rowIndex);
                    return (
                      <TableRow key={rowIndex} sx={{ bgcolor: isInvalid ? T.amberLt : 'inherit', '&:hover': { bgcolor: isInvalid ? T.amberBorder : T.bg } }}>
                        <TableCell sx={{ fontFamily: T.font, fontSize: 11, color: T.muted, position: 'sticky', left: 0, bgcolor: isInvalid ? T.amberLt : T.white, zIndex: 2, minWidth: 44, maxWidth: 44 }}>{rowIndex + 1}</TableCell>
                        {csvData.headers.map((header, cellIndex) => {
                          const isWide = ['notes', 'channel_partner_name', 'channel_partner_phone'].includes(header.toLowerCase());
                          return (
                            <TableCell key={`${rowIndex}-${cellIndex}`} sx={{ fontFamily: T.font, fontSize: 12, color: T.ink2, minWidth: isWide ? 150 : 100, maxWidth: isWide ? 240 : 200, whiteSpace: isWide ? 'pre-wrap' : 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'top' }}>
                              {row[header] || ''}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            {csvData.rows.length > 100 && !showAllRows && (
              <Button size="small" onClick={() => setShowAllRows(true)} endIcon={<ExpandMoreIcon />} sx={{ mt: 0.5, fontFamily: T.font, fontSize: 11, color: T.green, textTransform: 'none' }}>
                Show all {csvData.rows.length} rows
              </Button>
            )}
          </Box>
        )}

        {/* Mobile warnings */}
        {mobileValidationWarnings.length > 0 && (
          <Alert severity="warning" icon={<WarningIcon sx={{ fontSize: 18 }} />} sx={{ mb: 1.5, fontFamily: T.font, fontSize: 12, borderRadius: 2 }}>
            <strong>{mobileValidationWarnings.length} mobile number warning(s).</strong> Fix highlighted rows before import.
          </Alert>
        )}

        {/* Budget warnings */}
        {budgetValidationWarnings.length > 0 && (
          <Alert severity="warning" icon={<WarningIcon sx={{ fontSize: 18 }} />} sx={{ mb: 1.5, fontFamily: T.font, fontSize: 12, borderRadius: 2 }}>
            <strong>{budgetValidationWarnings.length} budget warning(s).</strong> Fix highlighted rows before import.
          </Alert>
        )}

        {/* Validation errors table */}
        {failedImports.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography sx={{ fontFamily: T.font, fontSize: 12, fontWeight: 700, color: T.red, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Validation Errors ({failedImports.length})
              </Typography>
              <Button
                size="small"
                variant="outlined"
                startIcon={<DownloadIcon sx={{ fontSize: 14 }} />}
                onClick={downloadCSVWithErrors}
                disabled={!originalCsvText}
                sx={{ fontFamily: T.font, fontSize: 11, textTransform: 'none', borderColor: T.border, color: T.subtle, borderRadius: 2, '&:hover': { borderColor: T.green, color: T.green } }}
              >
                Download errors CSV
              </Button>
            </Box>
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 250, borderRadius: 2, border: `1px solid ${T.redBorder}`, boxShadow: 'none' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {['Row', 'Name', 'Email', 'Mobile', 'Error'].map((h) => (
                      <TableCell key={h} sx={{ fontFamily: T.font, fontWeight: 700, fontSize: 11, color: T.subtle, bgcolor: T.redLt, borderBottom: `1px solid ${T.redBorder}` }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {failedImports.map((item, index) => {
                    const r = item.row || {};
                    const get = (key) => r[key] || r[key.charAt(0).toUpperCase() + key.slice(1)] || '';
                    return (
                      <TableRow key={index} sx={{ '&:hover': { bgcolor: T.redLt } }}>
                        <TableCell sx={{ fontFamily: T.font, fontSize: 12, color: T.muted }}>{item.row_number || item.rowNumber || '—'}</TableCell>
                        <TableCell sx={{ fontFamily: T.font, fontSize: 12, color: T.ink2 }}>{get('name') || '—'}</TableCell>
                        <TableCell sx={{ fontFamily: T.font, fontSize: 12, color: T.ink2, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{get('email') || '—'}</TableCell>
                        <TableCell sx={{ fontFamily: T.font, fontSize: 12, color: T.ink2, fontVariantNumeric: 'tabular-nums' }}>{get('mobile') || '—'}</TableCell>
                        <TableCell sx={{ fontFamily: T.font, fontSize: 12, color: T.red, fontWeight: 500, maxWidth: 300, whiteSpace: 'normal', wordBreak: 'break-word' }}>{item.error}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            {validationResults?.failed_imports_truncated && (
              <Typography sx={{ fontFamily: T.font, fontSize: 11, color: T.muted, mt: 0.5, fontStyle: 'italic' }}>
                Showing first 500 errors. Download CSV for full list.
              </Typography>
            )}
          </Box>
        )}

        {/* Download errors button when only client-side warnings exist */}
        {isValidated && (invalidMobileRows.size > 0 || invalidBudgetRows.size > 0) && failedImports.length === 0 && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon sx={{ fontSize: 14 }} />}
            onClick={downloadCSVWithErrors}
            disabled={!originalCsvText}
            sx={{ mb: 2, fontFamily: T.font, fontSize: 11, textTransform: 'none', borderColor: T.border, color: T.subtle, borderRadius: 2, '&:hover': { borderColor: T.green, color: T.green } }}
          >
            Download CSV with Errors
          </Button>
        )}

        {isUploading && (
          <Box sx={{ mb: 2 }}>
            <LinearProgress variant="determinate" value={uploadProgress} sx={{ height: 6, borderRadius: 3, bgcolor: T.borderL, '& .MuiLinearProgress-bar': { bgcolor: T.green, borderRadius: 3 } }} />
            <Typography sx={{ fontFamily: T.font, fontSize: 11, color: T.muted, textAlign: 'center', mt: 0.5 }}>{uploadProgress}%</Typography>
          </Box>
        )}

        {/* Action bar */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
          <Button
            onClick={() => { setActiveStep(0); setUploadError(null); setFileReadError(false); }}
            disabled={isUploading || isValidating}
            sx={{ fontFamily: T.font, fontSize: 12.5, fontWeight: 500, color: T.subtle, textTransform: 'none', borderRadius: 2 }}
          >
            Back
          </Button>
          <Button
            variant="outlined"
            onClick={handleValidate}
            disabled={!selectedFile || isValidating || isUploading || fileReadError || isValidated}
            startIcon={<CheckIcon sx={{ fontSize: 16 }} />}
            sx={{
              fontFamily: T.font, fontSize: 12.5, fontWeight: 600, textTransform: 'none',
              borderColor: T.green, color: T.green, borderRadius: 2,
              '&:hover': { bgcolor: T.greenSf, borderColor: T.greenMd },
              '&.Mui-disabled': { borderColor: T.borderL, color: T.muted }
            }}
          >
            {isValidating ? 'Validating...' : isValidated ? 'Validated ✓' : 'Validate CSV'}
          </Button>
          <Button
            variant="contained"
            onClick={() => handleImport(false)}
            disabled={!selectedFile || isUploading || isValidating || !isValidated || !validationResults || validationResults.total_successful === 0}
            startIcon={<UploadIcon sx={{ fontSize: 16 }} />}
            sx={{
              fontFamily: T.font, fontSize: 12.5, fontWeight: 600, textTransform: 'none',
              bgcolor: T.green, borderRadius: 2, boxShadow: 'none',
              '&:hover': { bgcolor: T.greenMd, boxShadow: 'none' },
              '&.Mui-disabled': { bgcolor: T.borderL, color: T.muted }
            }}
          >
            Create Leads ({validationResults?.total_successful ?? csvData.rows.length} rows)
          </Button>
        </Box>
      </Box>
    );
  };

  // ── Step 2: Results ─────────────────────────────────────────────────────

  const renderStep2 = () => {
    const isDone = importResults && (importResults.status === 'completed' || importResults.status === 'failed');
    const hasFailed = importResults?.status === 'failed';
    const successCount = importResults?.total_successful ?? validationResults?.total_successful ?? 0;
    const failCount = importResults?.total_failed ?? 0;

    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        {isDone ? (
          <>
            <Box sx={{ width: 64, height: 64, borderRadius: '50%', bgcolor: hasFailed ? T.redLt : T.greenLt, display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
              {hasFailed
                ? <ErrorIcon sx={{ fontSize: 32, color: T.red }} />
                : <CheckIcon sx={{ fontSize: 32, color: T.green }} />
              }
            </Box>
            <Typography sx={{ fontFamily: T.font, fontSize: 16, fontWeight: 700, color: T.ink, mb: 0.5 }}>
              {hasFailed ? 'Import failed' : 'Import complete'}
            </Typography>
            <Typography sx={{ fontFamily: T.font, fontSize: 13, color: T.subtle, mb: 3, maxWidth: 400, mx: 'auto' }}>
              {successCount > 0 && `${successCount} lead(s) created successfully.`}
              {successCount > 0 && failCount > 0 && ' '}
              {failCount > 0 && `${failCount} row(s) failed.`}
              {successCount === 0 && failCount === 0 && 'No leads were processed.'}
            </Typography>
          </>
        ) : (
          <>
            <Box sx={{ width: 64, height: 64, borderRadius: '50%', bgcolor: T.greenLt, display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2, animation: 'pulse 2s ease-in-out infinite', '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.5 } } }}>
              <UploadIcon sx={{ fontSize: 28, color: T.green }} />
            </Box>
            <Typography sx={{ fontFamily: T.font, fontSize: 16, fontWeight: 700, color: T.ink, mb: 0.5 }}>
              Import in progress…
            </Typography>
            <Typography sx={{ fontFamily: T.font, fontSize: 13, color: T.subtle, mb: 1.5, maxWidth: 400, mx: 'auto' }}>
              Leads are being created in the background. You can close this dialog — progress is tracked in the notification at the bottom-right.
            </Typography>
            <LinearProgress sx={{ mx: 'auto', maxWidth: 260, height: 4, borderRadius: 2, mb: 3, bgcolor: T.borderL, '& .MuiLinearProgress-bar': { bgcolor: T.green, borderRadius: 2 } }} />
          </>
        )}
        <Button
          variant="contained"
          onClick={handleDialogClose}
          sx={{
            fontFamily: T.font, fontSize: 13, fontWeight: 600, textTransform: 'none',
            bgcolor: T.green, borderRadius: 2, px: 4, boxShadow: 'none',
            '&:hover': { bgcolor: T.greenMd, boxShadow: 'none' }
          }}
        >
          {isDone ? 'Done' : 'Close'}
        </Button>
      </Box>
    );
  };

  const renderCheckingStatus = () => (
    <Box sx={{ py: 5, textAlign: 'center' }}>
      <LinearProgress sx={{ mx: 'auto', maxWidth: 220, height: 3, borderRadius: 2, bgcolor: T.borderL, '& .MuiLinearProgress-bar': { bgcolor: T.green, borderRadius: 2 } }} />
      <Typography sx={{ fontFamily: T.font, fontSize: 12.5, color: T.muted, mt: 1.5 }}>Checking upload status…</Typography>
    </Box>
  );

  const renderQueuedJob = () => {
    const previewRows = existingQueuedJob?.preview_rows || [];
    const totalRows = existingQueuedJob?.total_rows ?? 0;
    const remaining = totalRows - previewRows.length;
    const PREVIEW_COLS = ['name', 'email', 'mobile', 'source', 'stage'];

    return (
      <Box sx={{ py: 2 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <Box sx={{ width: 40, height: 40, borderRadius: '10px', bgcolor: T.amberLt, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <WarningIcon sx={{ fontSize: 20, color: T.amber }} />
          </Box>
          <Box>
            <Typography sx={{ fontFamily: T.font, fontSize: 14, fontWeight: 700, color: T.ink }}>
              Previous upload ready to import
            </Typography>
            <Typography sx={{ fontFamily: T.font, fontSize: 12, color: T.subtle }}>
              <strong>{totalRows} valid row(s)</strong> validated and waiting — commit now or upload a different file.
            </Typography>
          </Box>
          <Box sx={{ ml: 'auto', px: 1.5, py: 0.5, borderRadius: '8px', bgcolor: T.amberLt, border: `1px solid ${T.amberBorder}`, flexShrink: 0 }}>
            <Typography sx={{ fontFamily: T.font, fontSize: 12, fontWeight: 700, color: T.amber }}>{totalRows} rows</Typography>
          </Box>
        </Box>

        {/* Preview table */}
        {previewRows.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontFamily: T.font, fontSize: 10.5, fontWeight: 700, color: T.subtle, textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.75 }}>
              Preview
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, border: `1px solid ${T.borderL}`, boxShadow: 'none', maxHeight: 260 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontFamily: T.font, fontWeight: 700, fontSize: 11, color: T.subtle, bgcolor: T.bg, minWidth: 36, maxWidth: 36, py: 0.75 }}>#</TableCell>
                    {PREVIEW_COLS.map((col) => (
                      <TableCell key={col} sx={{ fontFamily: T.font, fontWeight: 700, fontSize: 11, color: T.subtle, bgcolor: T.bg, whiteSpace: 'nowrap', py: 0.75, textTransform: 'capitalize' }}>
                        {col}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {previewRows.map((row, idx) => (
                    <TableRow key={idx} sx={{ '&:hover': { bgcolor: T.bg } }}>
                      <TableCell sx={{ fontFamily: T.font, fontSize: 11, color: T.muted, py: 0.6 }}>{idx + 1}</TableCell>
                      {PREVIEW_COLS.map((col) => (
                        <TableCell key={col} sx={{ fontFamily: T.font, fontSize: 12, color: T.ink2, py: 0.6, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {row[col] || <span style={{ color: T.muted }}>—</span>}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {remaining > 0 && (
              <Typography sx={{ fontFamily: T.font, fontSize: 11, color: T.muted, mt: 0.5, fontStyle: 'italic' }}>
                + {remaining} more row(s) not shown in preview
              </Typography>
            )}
          </Box>
        )}

        {uploadError && (
          <Alert severity="error" sx={{ mb: 2, fontFamily: T.font, fontSize: 12.5, borderRadius: 2 }}>
            {uploadError}
          </Alert>
        )}

        {/* Actions */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button
            variant="outlined"
            onClick={async () => {
              try { await discardLeadImport(); } catch (e) { console.error('Error discarding import job:', e); }
              setExistingQueuedJob(null);
              setUploadError(null);
              setActiveStep(0);
            }}
            disabled={isUploading}
            sx={{ fontFamily: T.font, fontSize: 12.5, fontWeight: 600, textTransform: 'none', borderColor: T.border, color: T.subtle, borderRadius: 2, '&:hover': { borderColor: T.green, color: T.green, bgcolor: T.greenSf } }}
          >
            Upload new file
          </Button>
          <Button
            variant="contained"
            onClick={handleCommitExisting}
            disabled={isUploading}
            startIcon={<UploadIcon sx={{ fontSize: 16 }} />}
            sx={{ fontFamily: T.font, fontSize: 12.5, fontWeight: 600, textTransform: 'none', bgcolor: T.green, borderRadius: 2, boxShadow: 'none', '&:hover': { bgcolor: T.greenMd, boxShadow: 'none' }, '&.Mui-disabled': { bgcolor: T.borderL, color: T.muted } }}
          >
            {isUploading ? 'Starting…' : `Commit & Import (${totalRows} rows)`}
          </Button>
        </Box>
      </Box>
    );
  };

  const renderStepContent = () => {
    if (isCheckingStatus) return renderCheckingStatus();
    if (existingQueuedJob) return renderQueuedJob();
    switch (activeStep) {
      case 0: return renderStep0();
      case 1: return renderStep1();
      case 2: return renderStep2();
      default: return null;
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { maxHeight: '85vh', borderRadius: 3, fontFamily: T.font } }}
    >
      <DialogTitle sx={{ p: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, py: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ width: 32, height: 32, borderRadius: '8px', bgcolor: T.greenLt, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <DatasetIcon sx={{ fontSize: 18, color: T.green }} />
            </Box>
            <Typography sx={{ fontFamily: T.font, fontSize: 16, fontWeight: 700, color: T.ink }}>
              {title}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<DownloadIcon sx={{ fontSize: 14 }} />}
              onClick={downloadTemplateCSV}
              sx={{
                fontFamily: T.font, fontSize: 11.5, fontWeight: 600, textTransform: 'none',
                borderColor: T.border, color: T.subtle, borderRadius: 2, py: 0.5, px: 1.5,
                '&:hover': { borderColor: T.green, color: T.green, bgcolor: T.greenSf }
              }}
            >
              Sample CSV
            </Button>
            <IconButton size="small" onClick={handleDialogClose} sx={{ color: T.muted, '&:hover': { color: T.ink } }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <Divider sx={{ borderColor: T.borderL }} />

      <DialogContent sx={{ px: 3, py: 0 }}>
        <Stepper
          activeStep={activeStep}
          sx={{
            pt: 2, pb: 1,
            '& .MuiStepIcon-root': { color: T.borderL },
            '& .MuiStepIcon-root.Mui-active': { color: T.green },
            '& .MuiStepIcon-root.Mui-completed': { color: T.green },
            '& .MuiStepLabel-label': { fontFamily: T.font, fontSize: 12.5, fontWeight: 500, color: T.muted },
            '& .MuiStepLabel-label.Mui-active': { color: T.ink, fontWeight: 600 },
            '& .MuiStepLabel-label.Mui-completed': { color: T.green, fontWeight: 600 },
            '& .MuiStepConnector-line': { borderColor: T.borderL },
          }}
        >
          {steps.map((label) => (
            <Step key={label}><StepLabel>{label}</StepLabel></Step>
          ))}
        </Stepper>
        {renderStepContent()}
      </DialogContent>
    </Dialog>
  );
};

export default CSVImport;
